import React, { createContext, useContext } from 'react';
import {
	sendSubmissionQuery,
	useAxiosInstance,
	usePoMutation,
} from '@gov-nx/api';
import { GovError, useExtraConfigContext } from '@gov-nx/core/app';
import { useMessageEvents } from '@gov-nx/core/events';
import {
	useApplicantLoader,
	useDownloadBlob,
	useRezaSwitcher,
	useServiceAuthorization,
} from '@gov-nx/core/hooks';
import { getRequiredDataBoxes, useLocale } from '@gov-nx/core/service';
import type { Nullable } from '@gov-nx/core/types';
import { useAutorizaceContext } from 'libs/component/web/src/Autorizace/Context';
import type { ServiceCode } from '../definitions/codes';
import { FormInstance, prepareSubmitData } from './FormDefinitions';
import type { FormData, ServiceContextTypes } from './service.types';
import { filterDataBoxTypes } from './utils';
import { DEFAULT_PERSON_TYPE } from './constants';

const AUTHORIZATION_SERVICE_NAME = 'PODANI_OSVEDCENI_DIGITALNIHO_UKONU';

export const ServiceContext =
	createContext<Nullable<ServiceContextTypes>>(null);

export interface ServiceContextProviderProps {
	children: React.ReactNode;
	code: ServiceCode;
}

export function ZadostOOsvedceniDIGUkonuContextProvider({
	children,
	code,
}: ServiceContextProviderProps) {
	const { t } = useLocale(code);
	const { toastMessageSuccess } = useMessageEvents();
	const { handleDownloadBlob } = useDownloadBlob();
	const applicantLoader = useApplicantLoader();
	const rezaSwitcher = useRezaSwitcher({ code });
	const { zastupujiciOsobaTyp } = rezaSwitcher.activeSwitcherReza ?? {};
	const {
		extraConfig: { application },
	} = useExtraConfigContext();
	const requiredDataBoxes = getRequiredDataBoxes(code, application);
	const { axiosInstance } = useAxiosInstance();
	const personType = zastupujiciOsobaTyp ?? DEFAULT_PERSON_TYPE;
	const authContext = useAutorizaceContext();
	const { isLowAuthorization, isAuthorizationEnforced } =
		useServiceAuthorization(AUTHORIZATION_SERVICE_NAME);

	const submitMutation = usePoMutation({
		mutationKey: rezaSwitcher.blockingMutationKey,
		mutationFn: async (data: FormData) =>
			sendSubmissionQuery(axiosInstance)(prepareSubmitData(personType)(data)),
		onError: () => {
			formDefinition.formReset();
		},
		onSuccess: async (data) => {
			const nonHighAuthorizationFlow = () => {
				toastMessageSuccess(t('form.messages.odeslano'));
				formDefinition.formReset();
			};

			if (authContext) {
				authContext.onInitAuthorization(
					AUTHORIZATION_SERVICE_NAME,
					data.headers['x-autorizaceid'],
					nonHighAuthorizationFlow
				);
			} else {
				nonHighAuthorizationFlow();
			}
		},
	});

	const downloadBlob = handleDownloadBlob(() =>
		prepareSubmitData(personType)(formDefinition.formMethods.getValues())
	);

	const downloadMutation = usePoMutation({
		mutationKey: rezaSwitcher.blockingMutationKey,
		mutationFn: () =>
			new Promise<void>((resolve) => {
				downloadBlob.download(
					() => resolve(),
					() => resolve()
				);
			}),
		onSettled: () => {
			formDefinition.formReset();
		},
	});
	const formDefinition = FormInstance({
		code,
		isDisabled: submitMutation.isLoading || downloadBlob.isDownloading,
		isLowAuthorization,
	});

	const onSubmit = formDefinition.formMethods.handleSubmit((data) =>
		submitMutation.mutate(data)
	);
	const onDownload = formDefinition.formMethods.handleSubmit(() =>
		downloadMutation.mutate()
	);

	return (
		<ServiceContext.Provider
			value={{
				formDefinition,
				rezaSwitcher,
				controls: {
					initialError: applicantLoader.error
						? new GovError(applicantLoader.error.message)
						: null,
					initialLoading: applicantLoader.isLoading ?? false,

					processError: submitMutation.error,
					processLoading: submitMutation.isLoading,

					isDownloading: downloadBlob.isDownloading,
					downloadError: downloadBlob.error ?? null,
				},
				onSubmit,
				onDownload,
				requiredDataBoxes,
				dataBoxTypes: filterDataBoxTypes(requiredDataBoxes, personType),
				isLowAuthorization,
				isAuthorizationEnforced,
			}}>
			{children}
		</ServiceContext.Provider>
	);
}

export const useZadostOOsvedceniDigitalnihoUkonuContext = () =>
	useContext(ServiceContext) as ServiceContextTypes;
